import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Menu, Search, Plus, Bell, LayoutGrid, LogOut, UserCircle2, Building2 } from "lucide-react";
import { useCRMAuth } from "@/contexts/CRMAuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ws, fmtDate, gbp, ROLE_LABELS } from "./api";
import { useWorkspace } from "./WorkspaceContext";
import { Avatar } from "./ui";
import { AppLauncher } from "./AppLauncher";

const SearchBox = () => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const navigate = useNavigate();
  const timer = useRef();
  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setRes(null); return; }
    timer.current = setTimeout(() => ws(`/crm/workspace/search?q=${encodeURIComponent(q)}`).then(setRes).catch(() => setRes(null)), 250);
  }, [q]);
  const go = (path) => { setQ(""); setRes(null); navigate(path); };
  const total = res ? res.contacts.length + res.deals.length + res.employees.length : 0;
  return (
    <div className="relative flex-1 max-w-md hidden sm:block">
      <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search contacts, deals, employees"
        className="w-full h-9 pl-9 pr-3 rounded-lg bg-slate-100 border border-transparent text-sm focus:bg-white focus:border-slate-300 focus:outline-none transition-colors"
        data-testid="ws-search-input"
      />
      {res && (
        <div className="absolute top-11 left-0 right-0 bg-white rounded-xl border border-slate-200 shadow-xl z-40 py-2 max-h-96 overflow-y-auto" data-testid="ws-search-results">
          {total === 0 && <p className="px-4 py-3 text-sm text-slate-500">No matches for "{q}"</p>}
          {res.contacts.map((c) => (
            <button key={c.contact_id} onClick={() => go("/crm-app/sales/contacts")} className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm" data-testid={`search-contact-${c.contact_id}`}>
              <span className="text-[10px] uppercase text-slate-400 mr-2">Contact</span>{c.name} <span className="text-slate-400">{c.company}</span>
            </button>
          ))}
          {res.deals.map((d) => (
            <button key={d.deal_id} onClick={() => go("/crm-app/sales/pipeline")} className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm" data-testid={`search-deal-${d.deal_id}`}>
              <span className="text-[10px] uppercase text-slate-400 mr-2">Deal</span>{d.name} <span className="text-slate-400">{gbp(d.value)}</span>
            </button>
          ))}
          {res.employees.map((e) => (
            <button key={e.employee_id} onClick={() => go(`/crm-app/team?emp=${e.employee_id}`)} className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm" data-testid={`search-employee-${e.employee_id}`}>
              <span className="text-[10px] uppercase text-slate-400 mr-2">Employee</span>{e.first_name} {e.last_name} <span className="text-slate-400">{e.employee_id}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const QuickCreate = () => {
  const navigate = useNavigate();
  const { isAdmin } = useWorkspace();
  const items = [
    ["New deal", "/crm-app/sales/pipeline?create=deal", "qc-deal"],
    ["New contact", "/crm-app/sales/contacts?create=contact", "qc-contact"],
    ["New task", "/crm-app/tasks?create=task", "qc-task"],
    ["New meeting", "/crm-app/calendar?create=meeting", "qc-meeting"],
    ...(isAdmin ? [["New employee", "/crm-app/employees?create=employee", "qc-employee"], ["New announcement", "/crm-app/organization/announcements?create=1", "qc-announcement"]] : []),
  ];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="h-9 w-9 rounded-lg bg-[#0B1530] text-white flex items-center justify-center hover:bg-[#1a2650] transition-colors" data-testid="quick-create-btn">
          <Plus className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>Quick create</DropdownMenuLabel>
        {items.map(([l, p, t]) => <DropdownMenuItem key={t} onClick={() => navigate(p)} data-testid={t}>{l}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Notifications = () => {
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const navigate = useNavigate();
  const load = () => ws("/crm/workspace/notifications").then(setData).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  const open = async (o) => { if (o && data.unread) { await ws("/crm/workspace/notifications/read-all", { method: "POST" }).catch(() => {}); setTimeout(load, 400); } };
  return (
    <Popover onOpenChange={open}>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="notifications-btn">
          <Bell className="h-[18px] w-[18px] text-slate-600" />
          {data.unread > 0 && <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#E8A33D] text-[10px] font-bold text-[#0B1530] flex items-center justify-center" data-testid="notifications-unread">{data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b text-sm font-semibold">Notifications</div>
        <div className="max-h-80 overflow-y-auto">
          {data.notifications.length === 0 && <p className="px-4 py-6 text-sm text-slate-500 text-center">You're all caught up.</p>}
          {data.notifications.map((n) => (
            <button key={n.notification_id} onClick={() => n.link && navigate(n.link)} className="w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50">
              <p className={`text-sm ${n.read ? "text-slate-600" : "text-slate-900 font-medium"}`}>{n.title}</p>
              {n.body && <p className="text-xs text-slate-500">{n.body}</p>}
              <p className="text-[10px] text-slate-400 mt-1">{fmtDate(n.created_at, true)}</p>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const AvatarMenu = () => {
  const { me } = useWorkspace();
  const { logoutCRM } = useCRMAuth();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 rounded-lg pl-1 pr-2 py-1 hover:bg-slate-100" data-testid="avatar-menu-btn">
          <Avatar name={me?.name} size="h-8 w-8" />
          <span className="hidden md:block text-left leading-tight">
            <span className="block text-xs font-semibold text-slate-800">{me?.name}</span>
            <span className="block text-[10px] text-slate-500" data-testid="current-role-label">{ROLE_LABELS[me?.crm_role]}</span>
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal"><p className="text-sm font-medium">{me?.name}</p><p className="text-xs text-slate-500">{me?.email}</p></DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/crm-app/my-space")} data-testid="menu-my-account"><UserCircle2 className="h-4 w-4 mr-2" />My Account</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/crm-app/organization")} data-testid="menu-manage-org"><Building2 className="h-4 w-4 mr-2" />Manage Organization</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={async () => { navigate("/crm", { replace: true }); await logoutCRM(); }} data-testid="crm-logout-btn"><LogOut className="h-4 w-4 mr-2" />Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const TopBar = ({ onOpenMobile }) => {
  const [launcher, setLauncher] = useState(false);
  return (
    <header className="sticky top-0 z-30 h-16 bg-white/85 backdrop-blur-md border-b border-slate-200 flex items-center gap-3 px-4 sm:px-6" data-testid="ws-topbar">
      <button className="lg:hidden h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center" onClick={onOpenMobile} data-testid="sidebar-open-mobile">
        <Menu className="h-5 w-5" />
      </button>
      <Link to="/crm-app" className="lg:hidden font-brand text-sm font-bold tracking-[0.18em] text-[#0B1530]">VEYRONIX</Link>
      <SearchBox />
      <div className="ml-auto flex items-center gap-1.5">
        <QuickCreate />
        <Notifications />
        <button onClick={() => setLauncher(true)} className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="app-launcher-btn">
          <LayoutGrid className="h-[18px] w-[18px] text-slate-600" />
        </button>
        <AvatarMenu />
      </div>
      <AppLauncher open={launcher} onOpenChange={setLauncher} />
    </header>
  );
};
